/** NRC daily power percentages are observations for a report date, not capacity factors. */
export interface NrcDailyPowerSummary {
  reportDate: string | null;
  powerPct: number | null;
  reportedUnits: number;
  totalUnits: number;
  coverage: 'complete' | 'partial' | 'unavailable';
  units: { name: string; reportDate: string | null; powerPct: number | null; status: 'reported' | 'missing' | 'invalid' | 'conflicting' | 'older-date' }[];
}
export interface NrcPowerReading {
  reportDate: string;
  powerPct: number | null;
  status: 'reported' | 'invalid' | 'conflicting';
}
export interface NrcPowerSnapshot {
  latestReportDate: string | null;
  units: Map<string, NrcPowerReading>;
  invalidRows: number;
}

export function parseNrcReportDate(value: string): string | null {
  const m = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?: (\d{1,2}):(\d{2}):(\d{2}) (AM|PM))?$/);
  if (!m) return null;
  const [, mo, da, yr, hour, minute, second] = m;
  if (hour && (+hour < 1 || +hour > 12 || +minute! > 59 || +second! > 59)) return null;
  const date = new Date(Date.UTC(+yr!, +mo! - 1, +da!));
  return date.getUTCFullYear() === +yr! && date.getUTCMonth() === +mo! - 1 && date.getUTCDate() === +da!
    ? date.toISOString().slice(0, 10) : null;
}

/** Latest dated observation per canonical unit; conflicting/invalid latest rows stay unknown. */
export function parseReactorStatus(text: string, canonicalUnit: (name: string) => string = name => name,
  today = new Date().toISOString().slice(0, 10)): NrcPowerSnapshot {
  const result: NrcPowerSnapshot = { latestReportDate: null, units: new Map(), invalidRows: 0 };
  for (const line of text.replace(/^\uFEFF/, '').split(/\r?\n/)) {
    if (!line.trim() || line.trim() === 'ReportDt|Unit|Power') continue;
    const parts = line.split('|');
    const date = parts.length === 3 ? parseNrcReportDate(parts[0]!) : null;
    const name = parts[1]?.trim();
    if (!date || date > today || !name) { result.invalidRows++; continue; }
    const unit = canonicalUnit(name);
    const raw = parts[2]!.trim();
    const power = /^\d+(?:\.\d+)?$/.test(raw) ? Number(raw) : NaN;
    const powerPct = Number.isFinite(power) && power >= 0 && power <= 100 ? power : null;
    if (powerPct === null) result.invalidRows++;
    if (!result.latestReportDate || date > result.latestReportDate) result.latestReportDate = date;
    const previous = result.units.get(unit);
    if (!previous || date > previous.reportDate) {
      result.units.set(unit, { reportDate: date, powerPct, status: powerPct === null ? 'invalid' : 'reported' });
    } else if (date === previous.reportDate && (powerPct !== previous.powerPct || powerPct === null)) {
      result.units.set(unit, { reportDate: date, powerPct: null, status: 'conflicting' });
    }
  }
  return result;
}

export function summarizeNrcDailyPower(unitNames: string[], snapshot: NrcPowerSnapshot): NrcDailyPowerSummary {
  const units: NrcDailyPowerSummary['units'] = unitNames.map(name => {
    const reading = snapshot.units.get(name);
    const current = reading?.reportDate === snapshot.latestReportDate;
    return { name, reportDate: reading?.reportDate ?? null, powerPct: current ? reading?.powerPct ?? null : null,
      status: !reading ? 'missing' : !current ? 'older-date' : reading.status };
  });
  const known = units.filter(u => u.status === 'reported' && u.powerPct !== null);
  const complete = known.length > 0 && known.length === units.length;
  return { reportDate: snapshot.latestReportDate, reportedUnits: known.length, totalUnits: units.length,
    powerPct: complete ? Math.round(known.reduce((sum, u) => sum + u.powerPct!, 0) / known.length) : null,
    coverage: complete ? 'complete' : known.length ? 'partial' : 'unavailable', units };
}

export function formatNrcDailyPower(summary?: NrcDailyPowerSummary): string {
  if (!summary) return 'daily power and observation date unavailable (legacy undated fields)';
  const coverage = `${summary.reportedUnits}/${summary.totalUnits} catalog units reported${summary.reportDate ? ` on ${summary.reportDate}` : '; feed date unavailable'}`;
  if (summary.coverage === 'complete' && summary.powerPct !== null) return `${summary.powerPct}% daily reported power (unweighted unit mean; ${coverage})`;
  const observed = summary.units.filter(u => u.status === 'reported' && u.powerPct !== null)
    .map(u => `${u.name}: ${u.powerPct}%`).join(', ');
  return `site daily power unknown (${coverage}; ${summary.coverage}${observed ? `; reported units: ${observed}` : ''})`;
}
